import { useEffect, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * FounderBadge — small "First 100 users get Hush Plus for life · 37 spots left"
 * social-proof pill. Auto-hides when slots are depleted OR fetch fails (so we
 * never show a broken empty state).
 *
 * Renders ONLY when the public /api/founders/status endpoint returns open=true.
 * Cheap, ungated read — safe to call from Landing + Auth pages.
 */
export default function FounderBadge({ variant = "default" }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/founders/status");
        if (!cancelled) setStatus(data);
      } catch (_) { /* silent — feature is optional surface */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status?.open) return null;
  const { remaining, limit } = status;

  if (variant === "auth") {
    return (
      <div
        className="flex items-start gap-3 rounded-md border border-brand/30 bg-brand/[0.06] px-4 py-3 mb-4"
        data-testid="founder-badge-auth"
      >
        <Sparkle size={18} weight="fill" className="text-brand shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-ink-900">
            The first {limit} signups get <b>Hush Plus for life</b>.
          </p>
          <p className="text-ink-500 mt-0.5">
            {remaining} {remaining === 1 ? "spot" : "spots"} left — claim yours by creating your journal below.
          </p>
        </div>
      </div>
    );
  }

  // default (landing) variant — inline pill
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/[0.08] px-4 py-1.5 text-xs"
      data-testid="founder-badge-landing"
    >
      <Sparkle size={12} weight="fill" className="text-brand" />
      <span className="text-ink-900">
        <b>First {limit} signups get Hush Plus for life</b> · {remaining} {remaining === 1 ? "spot" : "spots"} left
      </span>
    </div>
  );
}
